import { Grid, MenuItem, Select, SelectChangeEvent, Tab, Table, TableBody, Typography } from "@mui/material";
import * as api from "api/Core";
import { DefaultListParameters } from "assets/appConfig";
import { AppMessageBox } from "components/AppMessageBox";
import { AppTabs } from "components/AppTabs";
import { SpinnerIndicator } from "components/CallServer";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiSelectFilter from "components/MuiExtras/MuiSelectFilter";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import LibUtils from "lib/LibUtils";
import { SystemRoles } from "models/ApiEnums";
import { DashboardGraphByGroupModel, DashboardKnowledgeIndicatorsSummaryColumn, DashboardKnowledgeTestAppliedDetailColumn, DashboardKnowledgeTestAppliedSummaryColumn, DashboardUserAndGroupsAndManagersBaseFilter, PagedResult } from "models/ApiModels";
import moment from "moment";
import * as React from "react";
import { WithTranslation } from "react-i18next";
import Session from "services/Session";
import { GraphLevels, KnowledgeTestHGraph } from "views/Dashboards/components/KnowledgeCommons";
import { RenderTestModelList } from "views/Dashboards/components/RenderTestModelList";
import { RenderTopicModelListEvents } from "views/Dashboards/components/RenderTopicModelList";
import { RenderUserModelListEvents } from "views/Dashboards/components/RenderUserModelList";
import { ParticipationListPublicationPeriod } from "views/Dashboards/UserAndGroupsDashboard/Common";
import CreateTest from "views/KnowledgeTest/CreateTest";

/////////////////////////////////////////////////////////////////////////////////////

type KnowledgeTestAppliedListOptionsType = {
    column: DashboardKnowledgeTestAppliedSummaryColumn;
    options: {
        key: DashboardKnowledgeTestAppliedDetailColumn;
        text: string;
        useLevel: boolean;
    }[];
};



/////////////////////////////////////////////////////////////////////////////////////

interface IGraphState {
    data: DashboardGraphByGroupModel;
    loading: boolean;
    selectedBarIndex: number;
    lastRequest: Date;
}

interface IGraphProps extends RenderUserModelListEvents, RenderTopicModelListEvents {
    filter: DashboardUserAndGroupsAndManagersBaseFilter;
    tile: DashboardKnowledgeTestAppliedSummaryColumn;
}

type GraphProps = IGraphProps & WithTranslation;

export class KnowledgeTestAppliedOthersGraph extends React.PureComponent<GraphProps, IGraphState>{

    constructor(props: GraphProps) {
        super(props);

        this.state = {
            data: null,
            loading: true,
            selectedBarIndex: null,
            lastRequest: new Date()
        };
    }

    ///
    private res(r: string) {
        return this.props.t("Views.Dashboards.KnowledgeTest.AppliedOthersGraph." + r);
    }

    ///
    private loadData() {
        var de = moment().endOf("day");
        var ds = moment().startOf("day").add(-12, "months");

        api.getDashboardKnowledgeTestApplied({
            columnToSummary: this.props.tile,
            detailColumn: DashboardKnowledgeTestAppliedDetailColumn.Graph,
            startDate: ds.toDate(),
            endDate: de.toDate(),
            range: null,
            ...this.props.filter
        })
            .then((result: DashboardGraphByGroupModel) => {
                this.setState({ data: result, loading: false, selectedBarIndex: null });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false, selectedBarIndex: null });
            });
    }

    ///
    private listOptions = 
        [
            {
                column: DashboardKnowledgeTestAppliedSummaryColumn.Result,
                options: [
                    { key: DashboardKnowledgeTestAppliedDetailColumn.TestMostWrong, text: this.res("TestMostWrongText"), useLevel: false },
                    { key: DashboardKnowledgeTestAppliedDetailColumn.TestMostRight, text: this.res("TestMostRightText"), useLevel: false },
                    { key: DashboardKnowledgeTestAppliedDetailColumn.TestResultRange, text: this.res("TestResultRangeText"), useLevel: true },
                    { key: DashboardKnowledgeTestAppliedDetailColumn.TestWrongHighTrust, text: this.res("TestWrongHighTrustText"), useLevel: false },
                    { key: DashboardKnowledgeTestAppliedDetailColumn.TestRightLowTrust, text: this.res("TestRightLowTrustText"), useLevel: true }
                ]
            },
            {
                column: DashboardKnowledgeTestAppliedSummaryColumn.Trust,
                options: [
                    { key: DashboardKnowledgeTestAppliedDetailColumn.TestLessTrust, text: this.res("TestLessTrustText"), useLevel: false },
                    { key: DashboardKnowledgeTestAppliedDetailColumn.TestMostTrust, text: this.res("TestMostTrustText"), useLevel: false },
                    { key: DashboardKnowledgeTestAppliedDetailColumn.TestTrustRange, text: this.res("TestTrustRangeText"), useLevel: true },
                ]
            }
        ];

    ///
    handleBarSelect = (index: number) => {
        this.setState({ selectedBarIndex: index, lastRequest: new Date() });
    };

    ///
    public componentDidMount() {
        this.loadData();
    }

    ///
    public componentDidUpdate(prevProps: GraphProps) {
        if (!LibUtils.isEqual(this.props, prevProps)) {
            this.loadData();
        }
    }

    ///
    public render() {
        const { tile } = this.props;
        const { data, loading } = this.state;

        if (loading)
            return <SpinnerIndicator />;

        if (data == null)
            return <AppMessageBox resource="LoadGraphDataFailed" />;

        const suffix = tile == DashboardKnowledgeTestAppliedSummaryColumn.Result ? "Result" : "Trust";

        return (
            <>
                <MuiContainer margin="dense" >
                    <Grid container justifyContent="space-between">
                        <Grid item>
                            <Typography component="p" variant="h6">
                                <b>
                                    {this.res(`HeaderTitle${suffix}`).toString().toLocaleUpperCase()}
                                </b>
                            </Typography>
                            <MuiMuted>
                                {this.res(`HeaderSubtitle${suffix}`)}
                            </MuiMuted>
                        </Grid>

                    </Grid>
                </MuiContainer>

                <MuiContainer margin="dense" >
                    <KnowledgeTestHGraph
                        tile={
                            tile == DashboardKnowledgeTestAppliedSummaryColumn.Result
                                ? DashboardKnowledgeIndicatorsSummaryColumn.Result
                                : DashboardKnowledgeIndicatorsSummaryColumn.Trust
                        }
                        data={data}
                        onBarSelected={this.handleBarSelect}
                    />
                </MuiContainer>

                <KnowledgeTestAppliedOthersList
                    {...this.props}
                    listOptions={this.listOptions}
                    selectedBar={this.state.selectedBarIndex}
                    lastRequest={this.state.lastRequest}
                />
            </>
        );
    }
}

////////////////////////////////////////////////////////////////////////////////////////

interface IListState {
    dialog: any;
    data: PagedResult<any>;
    offset: number;
    loading: boolean;
    tabOption: ParticipationListPublicationPeriod;
    primaryListOption: DashboardKnowledgeTestAppliedDetailColumn;
    secondaryListOption: number;
}

interface IListProps extends RenderUserModelListEvents, RenderTopicModelListEvents {
    tile: DashboardKnowledgeTestAppliedSummaryColumn;
    filter: DashboardUserAndGroupsAndManagersBaseFilter;
    listOptions: KnowledgeTestAppliedListOptionsType[];
    selectedBar?: number;
    lastRequest: Date;
}

type ListProps = IListProps & WithTranslation;

class KnowledgeTestAppliedOthersList extends React.PureComponent<ListProps, IListState> {

    constructor(props: ListProps) {
        super(props);

        this.state = {
            dialog: null,
            data: null,
            offset: null,
            loading: true,
            tabOption: ParticipationListPublicationPeriod.Last30,
            ...this.computeListOptions(),
        };
    }

    ///
    private getPrimaryListOptions() {
        return this.props.listOptions.find(p => p.column == this.props.tile).options;
    }

    ///
    private getSecondaryListOptions() {
        const column = this.props.tile == DashboardKnowledgeTestAppliedSummaryColumn.Result
            ? DashboardKnowledgeIndicatorsSummaryColumn.Result
            : DashboardKnowledgeIndicatorsSummaryColumn.Trust;

        var filter = GraphLevels(this.props.i18n).find(p => p.column == column);
        return filter.options;
    }

    ///
    private computeListOptions() {
        let tiles = this.getPrimaryListOptions();
        if (this.props.selectedBar != null) {
            tiles = tiles.filter(p => p.useLevel);
        }

        return { primaryListOption: tiles[0].key, secondaryListOption: this.props.selectedBar };
    }

    ///
    private loadData(offset: number) {

        this.setState({ loading: true });

        api.getDashboardKnowledgeTestApplied(
            {
                columnToSummary: this.props.tile,
                detailColumn: this.state.primaryListOption,
                selectedValue: this.state.secondaryListOption,
                range: { offset, take: DefaultListParameters.viewRecordsByPage },
                ...this.getDateFilter(),
                ...this.props.filter
            })
            .then((result) => {
                this.setState({ data: result, loading: false, offset: offset });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false, offset: 0 });
            });
    }

    ///
    private getDateFilter() {
        var lastDay = moment().utc().endOf("day");
        var firstDay = moment().utc().startOf("day");
        if (this.state.tabOption == ParticipationListPublicationPeriod.Last30)
            firstDay.add(-29, "days");
        else
            firstDay.add(-12, "months");

        return {
            startDate: firstDay.toDate(),
            endDate: lastDay.toDate()
        };
    }

    ///
    private handleListOption = (e: SelectChangeEvent<DashboardKnowledgeTestAppliedDetailColumn>) => {
        var g = GraphLevels(this.props.i18n);
        this.setState({
            primaryListOption: e.target.value as DashboardKnowledgeTestAppliedDetailColumn,

            secondaryListOption:
                e.target.value == DashboardKnowledgeTestAppliedDetailColumn.TestResultRange
                    ? g.find(p => p.column == DashboardKnowledgeIndicatorsSummaryColumn.Result).options[0].key
                    : e.target.value == DashboardKnowledgeTestAppliedDetailColumn.TestTrustRange
                        ? g.find(p => p.column == DashboardKnowledgeIndicatorsSummaryColumn.Trust).options[0].key
                        : null
        }, () => this.loadData(0));
    };


    ///
    private handleSecondaryListOption = (e: SelectChangeEvent<number>) => {
        this.setState({ secondaryListOption: e.target.value as number, tabOption: ParticipationListPublicationPeriod.All }, () => this.loadData(0));
    };

    ///
    private handleSelectedTest = (testId: string, title: string) => {

        if (Session.hasRole(SystemRoles.TrainingManager)) {
            this.setState({
                dialog: (
                    <CreateTest
                        testId={testId}
                        onClose={
                            () => this.setState({ dialog: null })
                        }
                    />
                )
            });
        }
    };

    ///
    private renderTitle() {

        return (
            <MuiContainer margin="dense" >
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item>

                        <Select
                            value={this.state.primaryListOption}
                            onChange={this.handleListOption}
                        >
                            {
                                this.getPrimaryListOptions().map((v, index) => <MenuItem key={index} value={v.key}>{v.text}</MenuItem>)
                            }
                        </Select>

                    </Grid>
                    {
                        this.state.primaryListOption == DashboardKnowledgeTestAppliedDetailColumn.TestResultRange
                            || this.state.primaryListOption == DashboardKnowledgeTestAppliedDetailColumn.TestTrustRange
                            ? (
                                <Grid item>
                                    <MuiSelectFilter
                                        value={this.state.secondaryListOption}
                                        onChange={this.handleSecondaryListOption}
                                    >
                                        {
                                            this.getSecondaryListOptions().map((v, index) => <MenuItem key={index} value={v.key}>{v.text}</MenuItem>)
                                        }
                                    </MuiSelectFilter>
                                </Grid>
                            )
                            : (
                                <Grid item>
                                    <AppTabs
                                        value={this.state.tabOption}
                                        onChange={
                                            (e, newValue: ParticipationListPublicationPeriod) => {
                                                this.setState({ tabOption: newValue }, () => this.loadData(0));
                                            }
                                        }
                                        indicatorColor="primary"
                                        textColor="primary"
                                    >
                                        <Tab label={this.props.t<string>("Views.Dashboards.Common.LastMonth")} value={ParticipationListPublicationPeriod.Last30} />
                                        <Tab label={this.props.t<string>("Views.Dashboards.Common.FullPeriod")} value={ParticipationListPublicationPeriod.All} />
                                    </AppTabs>
                                </Grid>
                            )
                    }
                </Grid>
            </MuiContainer>
        );
    }

    ///
    private renderList() {
        const { data: { items, total }, offset, dialog } = this.state;


        return (
            <MuiContainer margin="normal">
                <>
                    {dialog}
                    <br />
                    {this.renderTitle()}

                    {
                        total == 0
                            ? (
                                <MuiMessageBox variant="warning" message={this.props.t("Views.Dashboards.KnowledgeTest.AppliedOthersList.EmptyData")} />
                            )
                            : (
                                <>
                                    <MuiContainer>
                                        <Table size="small" >
                                            <TableBody>
                                                <RenderTestModelList indexOffset={offset} items={items} onSelectTest={this.handleSelectedTest} />
                                            </TableBody>
                                        </Table>
                                    </MuiContainer>
                                    <MuiPagination
                                        limit={DefaultListParameters.viewRecordsByPage}
                                        offset={this.state.offset}
                                        total={total}
                                        onClick={(e, offset) => this.loadData(offset)}
                                    />
                                </>
                            )
                    }
                </>
            </MuiContainer >
        );
    }

    ///
    private checkForUpdateState(prevProps: ListProps) {
        if (!LibUtils.isEqual(this.props, prevProps)) {
            if (this.props.tile != prevProps.tile) {
                const listOptions = this.getPrimaryListOptions();
                this.setState({ primaryListOption: listOptions[0].key, secondaryListOption: null }, () => this.loadData(0));
            }
            else {
                this.setState({ ...this.computeListOptions() }, () => this.loadData(0));
            }
        }
    }

    ///
    public componentDidMount() {
        this.loadData(0);
    }

    ///
    public componentDidUpdate(prevProps: ListProps) {
        this.checkForUpdateState(prevProps);
    }

    ///
    public render() {

        if (this.state.loading)
            return <SpinnerIndicator />;

        if (this.state.data == null)
            return <AppMessageBox resource="LoadListDataFailed" />;

        return this.renderList();
    }
}